import {
	Dispatch,
	MutableRefObject,
	SetStateAction,
	useCallback,
	useState,
} from 'react';
import { IPanToBounds } from 'models';
import {
	ICommentsAverage,
	IPointWithOffset,
} from 'modules/admin-panel/pages/results/models';
import {
	areOfficePointsTheSame,
	getCityAndOfficeCoordinates,
	officeAndCityCoordinatesEqual,
	officeCoordinatesEqualForAllPoints,
} from 'helpers';

export const useMapBounding = (
	mapRef: MutableRefObject<google.maps.Map | undefined>,
	setPrevMapZoom: Dispatch<SetStateAction<number | undefined>>,
) => {
	// add to prevent multiple zoom in in case when office and city coordinates of the point are the same
	const [lastSelectedOffice, setLaseSelectedOffice] = useState<
		ICommentsAverage
	>();

	const panTo = (lat: number, lng: number, zoom: number) => {
		mapRef.current?.setCenter({ lat, lng });
		mapRef.current?.setZoom(zoom);
		setPrevMapZoom(zoom);
	};

	const panToBounds = async ({ sw, ne }: IPanToBounds) => {
		const SW = new google.maps.LatLng(sw.lat as number, sw.lng as number);
		const NE = new google.maps.LatLng(ne.lat as number, ne.lng as number);
		const bounds = new google.maps.LatLngBounds(SW, NE);

		await mapRef.current?.fitBounds(bounds);
		setPrevMapZoom(mapRef.current?.getZoom());
	};

	const panToGroupBounds = async (mapPoints: ICommentsAverage[]) => {
		const bounds = new google.maps.LatLngBounds();
		mapPoints.forEach((point) => {
			if (!point.latitude || !point.longitude) return;
			const pointBound = new google.maps.LatLng(
				point.latitude,
				point.longitude,
			);
			bounds.extend(pointBound);
		});

		await mapRef.current?.fitBounds(bounds);
		setPrevMapZoom(mapRef.current?.getZoom());
	};

	const zoomToLevel = useCallback(
		(level: number, points: ICommentsAverage[]) => {
			points.length > 1 && !officeCoordinatesEqualForAllPoints(points)
				? panToGroupBounds(points)
				: panTo(points[0].latitude, points[0].longitude, level);
		},
		[],
	);

	const zoomSelectedOffice = (selectedItem: IPointWithOffset) => {
		if (!mapRef.current) return;
		if (
			lastSelectedOffice &&
			areOfficePointsTheSame(selectedItem, lastSelectedOffice)
		)
			return;

		if (officeAndCityCoordinatesEqual(selectedItem)) {
			const newZoom = mapRef.current.getZoom() + 1;
			const { latitude, longitude } = selectedItem;
			panTo(latitude, longitude, newZoom);
			setLaseSelectedOffice(selectedItem);
		} else {
			const {
				cityCoordinates,
				officeCoordinates,
			} = getCityAndOfficeCoordinates(selectedItem);

			panToBounds(
				officeCoordinates.sw.lat === null ? cityCoordinates : officeCoordinates,
			);
		}
	};

	return {
		panToGroupBounds,
		zoomToLevel,
		zoomSelectedOffice,
	};
};
